/**
 * Pure routing/pin-identity helpers for the P6b keep-alive TabbedAppShell.
 *
 * Kept dependency-free (no React, no @astrana/ui) so they're unit-testable in
 * isolation without dragging the whole member-profile module tree (and its
 * @astrana/ui build) into the test transform.
 */

const PROFILE_PATH_RE = /^\/members\/([^/]+)$/;

/** The raw (still-URL-encoded) `:id` segment of a `/members/:id` path, if any. */
export function profileSegment(pathname: string): string | undefined {
  return PROFILE_PATH_RE.exec(pathname)?.[1];
}

export type ActiveTab =
  | "home"
  | "search"
  | "profile"
  | "worklist"
  | "schedule"
  | "hospitalEd";

/**
 * Map a browser pathname to the active keep-alive tab. `null` → the path isn't
 * a valid tab (unknown path caught by the `/*` catch-all, or `/worklist` while
 * the worklist is gated off) and the shell should redirect to search.
 */
export function activeTabForPath(
  pathname: string,
  worklistEnabled: boolean,
): ActiveTab | null {
  // Home is the app's default landing tab (App.tsx redirects `/` here). No
  // enable flag and no persona gate — it's always a valid tab, same as
  // /members always maps to "search" below.
  if (pathname === "/home" || pathname.startsWith("/home/")) {
    return "home";
  }
  if (pathname === "/worklist" || pathname.startsWith("/worklist/")) {
    return worklistEnabled ? "worklist" : null;
  }
  // Hospital/ED — same underlying panel/gate as /worklist (see
  // HospitalEdGate/HospitalEdPage), so it shares the SAME `worklistEnabled`
  // flag+persona check rather than a separate one.
  if (pathname === "/hospital-ed" || pathname.startsWith("/hospital-ed/")) {
    return worklistEnabled ? "hospitalEd" : null;
  }
  // Practice Schedule (ACT-1239) — a real persona-gated top-level route.
  // Unlike /worklist it carries no routing-level enable flag: the path always
  // maps to the "schedule" tab (so a deep link resolves), and persona gating is
  // enforced by PracticeScheduleGate on the page body + the nav tab's own
  // visibility check — exactly how /members always maps to "search" while
  // MemberSearchGate governs the page.
  if (pathname === "/schedule" || pathname.startsWith("/schedule/")) {
    return "schedule";
  }
  if (profileSegment(pathname) !== undefined) return "profile";
  if (pathname === "/" || pathname === "/members") return "search";
  return null;
}

/**
 * Should the shell step off Home so the chart's patient can auto-navigate?
 *
 * WHY this exists: `/home` became the app's default landing route (and so the
 * extension side panel's boot location, since the panel iframes the app at
 * "/"). Member Search stays MOUNTED behind Home in the keep-alive shell, but
 * its two auto-navigate effects are gated on `isActiveTab` — only the VISIBLE
 * tab may call `navigate()` (see MemberSearchPage's Req 1 / Req 2 and the
 * hidden-tab race they guard against). Landing on Home therefore left the
 * in-context Elation patient's auto-search running invisibly with its
 * navigation swallowed: auto-nav looked simply dead, with no error anywhere.
 *
 * The fix keeps that invariant rather than punching a hole in it — no hidden
 * tab gains the right to navigate. Instead, when a chart is genuinely in the
 * FOREGROUND, the shell selects the Search tab, so auto-nav runs on a tab the
 * clinician can actually see. No chart in front → Home stays Home.
 *
 * ONCE PER PATIENT (`lastRedirectedPatientId`): otherwise a chart whose patient
 * never resolves to a member would bounce the user straight back to Search
 * every time they clicked Home — the chart stays foreground, so the condition
 * stays true forever. Remembering the patient we already stepped aside for
 * makes this a single, followable move; a genuinely DIFFERENT chart re-arms it,
 * which is the same "follow the foreground chart" rule the auto-nav guard
 * itself uses.
 */
export function shouldLeaveHomeForChart(args: {
  /** The tab the shell is currently showing. */
  activeTab: ActiveTab | null;
  /** The foreground chart's EHR patient id, or null when no chart is in front. */
  chartPatientId: string | null;
  /** The chart we have already stepped off Home for, if any. */
  lastRedirectedPatientId: string | null;
}): boolean {
  const { activeTab, chartPatientId, lastRedirectedPatientId } = args;
  if (activeTab !== "home") return false;
  if (!chartPatientId) return false;
  return chartPatientId !== lastRedirectedPatientId;
}
